import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SHEER_PAGE_TYPES, SHEER_PAGE_STATUS, isSheerIdPlan } from 'Utils/SheerIdHelpers';
import { SheerIdFormPage } from './SheerIdFormPage/SheerIdFormPage';
import { SheerIdUploadPage } from './SheerIdUpload/SheerIdUploadPage';
import { SheerIdReviewPage } from './SheerIdReviewPage/SheerIdReviewPage';
import { SheerIdStatusPage } from './SheerIdStatusPage/SheerIdStatusPage';
import './SheerIdVerification.css';
import { SHEER_FIRST_RESPONDER_PACKAGE_ID, SHEER_MILITARY_PACKAGE_ID } from 'Utils/Constants';
import { selectPlan } from 'State/Plans';
import { useStore } from 'Hooks/useStore';
import { addRedirectUrl, getUserSheerInfo, setSheerProgram, setSheerVerificationId } from 'State/Integrations';

const components = {
  [SHEER_PAGE_TYPES.FORM]: SheerIdFormPage,
  [SHEER_PAGE_TYPES.DOC_UPLOAD]: SheerIdUploadPage,
  [SHEER_PAGE_TYPES.REVIEW]: SheerIdReviewPage,
  [SHEER_PAGE_TYPES.SUCCESS]: SheerIdStatusPage,
  [SHEER_PAGE_TYPES.ERROR]: SheerIdStatusPage,
  [SHEER_PAGE_TYPES.INELIGIBLE]: SheerIdStatusPage,
};

const STATUS_PAGES = [SHEER_PAGE_TYPES.SUCCESS, SHEER_PAGE_TYPES.ERROR, SHEER_PAGE_TYPES.INELIGIBLE];

export const SheerIdVerificationPage = () => {
  const { bango, sheerId } = useStore('integrations');
  const { verificationId } = sheerId;
  const { isStatusSuccess, hasActiveSubscription, purchaseType, activeSubscriptionId, isChurnedUser } =
    useStore('subscription');
  const { allPlans, getPlansSuccess } = useStore('plans');
  const { step } = sheerId;
  const [pageProps, setPageProps] = useState({
    pageType: (step && SHEER_PAGE_STATUS[step]) || SHEER_PAGE_TYPES.FORM,
    /* the params are either the response/error from getUserSheerInfo or an error from elsewhere
      if response, will look like:
      { verificationId, currentStep, program, partner, bangoRedirectUrl: optional }
    */
    params: {},
  });

  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');
  const location = useLocation();
  const { pathname } = location;
  const navigate = useNavigate();
  const pageType = pageProps?.pageType;
  const isErrorPage = pageType === SHEER_PAGE_TYPES.ERROR;
  const stepParams = pageProps?.params;
  const { partner } = stepParams;
  const { redirectUrl } = bango;
  const hasBackBangoButton =
    redirectUrl && (pageType === SHEER_PAGE_TYPES.FORM || pageType === SHEER_PAGE_TYPES.REVIEW);
  const hasBackToVerification = pageType === SHEER_PAGE_TYPES.DOC_UPLOAD || pageType === SHEER_PAGE_TYPES.ERROR;
  const isSheerIdStatusPage =
    [SHEER_PAGE_TYPES.SUCCESS, SHEER_PAGE_TYPES.ERROR, SHEER_PAGE_TYPES.INELIGIBLE].indexOf(pageType) > -1;

  const { isAuthenticated } = useStore('user');
  const routes = useStore('appConfig.routes');
  let redirectToPath = routes.signupCreateAccount;
  // partner is a prop passed with the step that tells which kind of user someone is fox = nonbango
  if (isAuthenticated && partner === 'Fox') {
    redirectToPath = routes.payment;
  }

  const setPage = useCallback(
    (newPageType, newStepParams) => {
      setLoading(STATUS_PAGES.includes(newPageType));
      // Defaults when memberVerify is 404ing before form submission
      setPageProps({
        pageType: newPageType || SHEER_PAGE_TYPES.FORM,
        params: newStepParams || {},
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [setPageProps]
  );

  useEffect(() => {
    const getVerificationStep = async () => {
      // Returns step, and other data associated with a user with a verification Id,
      setLoading(true);
      try {
        const verificationStep = await getUserSheerInfo();
        if (verificationStep?.bangoRedirectUrl) addRedirectUrl(verificationStep.bangoRedirectUrl);
        setPage(SHEER_PAGE_STATUS[verificationStep?.currentStep], verificationStep);
      } catch (error) {
        if (error?.currentStep) {
          setPage(SHEER_PAGE_STATUS[error?.currentStep], error);
        } else {
          setLoading(false);
        }
      }
    };

    getVerificationStep();
  }, [setPage]);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const sheerProgramType = urlParams.get('sheerVerification') || pageProps.params.segment;
    if (sheerProgramType) setSheerProgram(sheerProgramType);
    if (pageProps.params.verificationId) setSheerVerificationId(pageProps.params.verificationId);
  }, [pageProps.params, location.search]);

  useEffect(() => {
    // no redirection happens until user subscription status is fetched
    if (!isStatusSuccess) return () => {};

    // existing Sheer or Bango users are ineligible
    if (hasActiveSubscription && (isSheerIdPlan(activeSubscriptionId) || purchaseType === 'bango')) {
      if (pageType !== SHEER_PAGE_TYPES.INELIGIBLE) {
        setPage(SHEER_PAGE_TYPES.INELIGIBLE, stepParams);
      }

      // else, run navigation logic taken from SheerIdStatusPage
      // This is to navigate to the payment page for non-bango users with the preselected id
    } else if (isSheerIdStatusPage) {
      const hasSheerIdPackages =
        getPlansSuccess && allPlans.length && allPlans.some((plan) => isSheerIdPlan(plan.packageID));

      const selectSheerPlan = () => {
        let sheerPlan;
        if (allPlans.length && verificationId) {
          sheerPlan = allPlans.find((plan) =>
            [SHEER_MILITARY_PACKAGE_ID, SHEER_FIRST_RESPONDER_PACKAGE_ID].includes(plan.packageID)
          );
          if (sheerPlan) {
            selectPlan(sheerPlan.packageID);
          }
        }
        return sheerPlan;
      };

      if ((partner === 'Fox' || isChurnedUser) && !isErrorPage) {
        const sheerPlan = !isChurnedUser && selectSheerPlan();
        const packageId = sheerPlan?.packageID;
        if (hasSheerIdPackages && packageId) {
          if (allPlans.find((plan) => plan.packageID === packageId)) {
            navigate(`${redirectToPath}/${packageId}`);
          }
        } else if (hasSheerIdPackages) {
          navigate(redirectToPath, { state: { from: pathname } });
        }
      }
    }
  }, [
    isStatusSuccess,
    hasActiveSubscription,
    activeSubscriptionId,
    purchaseType,
    setPage,
    stepParams,
    isSheerIdStatusPage,
    getPlansSuccess,
    allPlans,
    partner,
    isErrorPage,
    verificationId,
    navigate,
    redirectToPath,
    pathname,
    pageType,
    isChurnedUser,
  ]);

  const layoutProps = useMemo(
    () => ({
      title,
      showLoader: loading,
      modernLayout: true,
      panelStyle: hasBackBangoButton || hasBackToVerification ? { marginTop: 20 } : {},
      customFrameClass: 'sheerIdFrameContainer',
    }),
    [title, loading, hasBackBangoButton, hasBackToVerification]
  );

  const Component = components[pageType];

  return (
    <Component
      setPage={setPage}
      pageType={pageType}
      setTitle={setTitle}
      setLoading={setLoading}
      layoutProps={layoutProps}
      path={redirectToPath}
      {...stepParams}
    />
  );
};
